import React from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import LoopIcon from '@material-ui/icons/Loop';
import frontImage from '../../../Assets/images/PoseMy.Art (3).png';
import backImage from '../../../Assets/images/PoseMy.Art (4).png';

// ─── Percentage-based coordinates for each body region on both views ────────
// Values are {x, y} expressed as percentages of the image width/height.
// Front view = PoseMy.Art (3).png, Back view = PoseMy.Art (4).png
const BODY_REGION_COORDS = {
  head_top:            { front: { x: 50, y: 5 },   back: { x: 50, y: 5 } },
  head_left:           { front: { x: 44, y: 8 },   back: { x: 56, y: 8 } },   // mirrored on back
  head_right:          { front: { x: 56, y: 8 },   back: { x: 44, y: 8 } },
  forehead:            { front: { x: 50, y: 7 },   back: null },
  temple_left:         { front: { x: 43, y: 8.5 }, back: { x: 57, y: 8.5 } },
  temple_right:        { front: { x: 57, y: 8.5 }, back: { x: 43, y: 8.5 } },
  jaw:                 { front: { x: 50, y: 13 },  back: null },
  neck:                { front: { x: 49.7, y: 17.5 },  back: { x: 49.6, y: 15 } },
  neck_back:           { front: null,               back: { x: 49.6, y: 15 } },
  shoulder_left:       { front: { x: 34, y: 22 },  back: { x: 66, y: 22 } },
  shoulder_right:      { front: { x: 66, y: 22 },  back: { x: 34, y: 22 } },
  shoulder_blade_left: { front: null,               back: { x: 60, y: 27 } },
  shoulder_blade_right:{ front: null,               back: { x: 40, y: 27 } },
  shoulder_blades:     { front: null,               back: { x: 50, y: 27 } },
  upper_arm_left:      { front: { x: 38.5, y: 34 },  back: { x: 61.7, y: 34 } },
  upper_arm_right:     { front: { x: 62.5, y: 34 },  back: { x: 37.7, y: 34 } },
  arm_left:            { front: { x: 34.5, y: 42 },  back: { x: 65.5, y: 42 } },
  arm_right:           { front: { x: 65.5, y: 42 },  back: { x: 34.5, y: 42 } },
  hand_left:           { front: { x: 30, y: 54 },  back: { x: 70, y: 54 } },
  hand_right:          { front: { x: 70, y: 54 },  back: { x: 30, y: 54 } },
  upper_back:          { front: null,               back: { x: 50, y: 26 } },
};

// ─── Diagnosis → annotation config ────────────────────────────────────────────
// Each diagnosis maps to primary dots and optional radiation targets.
// `lateralityKey` indicates which assessment answer determines left/right/both.
const DIAGNOSIS_ANNOTATION_MAP = {
  // ── Neck diagnoses ──
  NonSpecificNeckPain_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back'],
  },
  NonSpecificNeckPain_withRefPain_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [
      { from: 'neck', to: 'shoulder_blades', bilateral: true },
      { from: 'neck', to: 'arm', lateral: true },
    ],
    backDots: ['neck_back', 'shoulder_blades'],
    lateralityKey: 'symptomsLeftRightBothArms',
  },
  NonspecificNeckPain_withMobilityDeficitsACUTE_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back', 'upper_back'],
  },
  NonspecificNeckPain_withMobilityDeficitsRECURRENT_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back', 'upper_back'],
  },
  NeckPain_withRadicularPain_cervical_radiculopathy_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [
      { from: 'neck', to: 'arm', lateral: true },
      { from: 'neck', to: 'shoulder_blades', bilateral: true },
      { from: 'arm', to: 'hand', lateral: true },
      { from: 'upper_arm', to: 'hand', lateral: true },
    ],
    backDots: ['neck_back'],
    lateralityKey: 'symptomsLeftRightBothArms',
  },
  DiscogenicNeckPain_withReferredArmPain_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [
      { from: 'neck', to: 'shoulder_blades', bilateral: true },
      { from: 'neck', to: 'upper_arm', lateral: true },
      { from: 'upper_arm', to: 'hand', lateral: true },
    ],
    backDots: ['neck_back', 'shoulder_blades'],
    lateralityKey: 'symptomsLeftRightBothArms',
  },
  DiscogenicNeckPain_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back'],
  },
  CervicalMuscleStrain_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back'],
  },
  NeckPain_withMovementCoordinationImpairments_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back'],
  },
  NeckArthritis_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [],
    backDots: ['neck_back'],
  },

  // ── Headache / migraine diagnoses ──
  chronic_Migraine_Diagnosis: {
    primaryDots: ['head_left'],
    radiationTargets: [
      { from: 'head_left', to: 'temple_left', bilateral: false },
    ],
    backDots: [],
  },
  migraine_WithAura_Diagnosis: {
    primaryDots: ['head_left'],
    radiationTargets: [],
    backDots: [],
  },
  migraine_WithoutAura_Diagnosis: {
    primaryDots: ['head_left'],
    radiationTargets: [],
    backDots: [],
  },
  EpisodicTensionTypeHeadache_Diagnosis: {
    primaryDots: ['head_left', 'head_right'],
    radiationTargets: [],
    backDots: [],
  },
  ChronicTensionTypeHeadache_Diagnosis: {
    primaryDots: ['head_left', 'head_right'],
    radiationTargets: [],
    backDots: [],
  },
  Cervicogenic_Headache_Diagnosis: {
    primaryDots: ['neck'],
    radiationTargets: [
      { from: 'neck', to: 'head_right', bilateral: false },
    ],
    backDots: ['neck_back'],
  },
  ClusterHeadache_with_HornerSyndrome_Diagnosis: {
    primaryDots: ['head_right', 'temple_right'],
    radiationTargets: [],
    backDots: [],
  },
  ClusterHeadache_without_HornerSyndrome_Diagnosis: {
    primaryDots: ['head_right', 'temple_right'],
    radiationTargets: [],
    backDots: [],
  },
  MedicationOveruseHeadache_Diagnosis: {
    primaryDots: ['head_left', 'head_right'],
    radiationTargets: [],
    backDots: [],
  },
  Exertion_Headache_Diagnosis: {
    primaryDots: ['head_top'],
    radiationTargets: [],
    backDots: [],
  },

  // ── TMJ / Jaw ──
  TemporomandibularJointDisorder_Diagnosis: {
    primaryDots: ['jaw'],
    radiationTargets: [
      { from: 'jaw', to: 'temple_left', bilateral: false },
      { from: 'jaw', to: 'temple_right', bilateral: false },
    ],
    backDots: [],
  },

  // ── Dizziness / Vestibular ──
  CervicogenicDizziness_Diagnosis: {
    primaryDots: ['head_top', 'neck'],
    radiationTargets: [],
    backDots: ['neck_back'],
  },
  BenignParoxysmalPositionelVertigo_Diagnosis: {
    primaryDots: ['head_top'],
    radiationTargets: [],
    backDots: [],
  },

  // ── Shoulder diagnoses ──
  FrozenShoulder_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  BicepsTendinopathy_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [
      { from: 'shoulder_right', to: 'upper_arm_right', bilateral: false },
    ],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  BicepsTendonRupture_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [
      { from: 'shoulder_right', to: 'upper_arm_right', bilateral: false },
    ],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  ShoulderDislocation_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  SupraInfraspinatusTendinopathy_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  SupraInfraspinatusPartialTendonRupture_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  SupraInfraspinatusFULLTendonRupture_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  SubscapularisFULLTendonRupture_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  SubscapularisPARTIALTendonRupture_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  subscapularisTendinopathy_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  ShoulderBursitis_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  secondaryShoulderImpingement_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  AnteriorInstability_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  AnteriorInstabilityWithSLAPlesion_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  MultiDirectionalInstability_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  PosteroinferiorInstability_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  NonTraumaticShoulderInstability_AMBRI_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  Anterior_Shoulder_Dislocation_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  Posterior_Shoulder_Dislocation_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: ['shoulder_blade_right'],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  ACJ_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  ACJinjury_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  ClavicleFractureInjury_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
  ProvimalHumerusFractureInjury_Diagnosis: {
    primaryDots: ['shoulder_right'],
    radiationTargets: [
      { from: 'shoulder_right', to: 'upper_arm_right', bilateral: false },
    ],
    backDots: [],
    lateralityKey: 'whichPartOfShoulderHurts',
  },
};

// ─── Resolve laterality from assessment answers ─────────────────────────────
// Returns 'left' | 'right' | 'both' | null
function resolveLaterality(symptomLocations, lateralityKey) {
  if (!symptomLocations || !lateralityKey) return null;
  const answer = symptomLocations[lateralityKey];
  if (!answer) return null;
  // Common answer patterns from the assessment:
  // "a" = left, "b" = right, "c" = both  (for symptomsLeftRightBothArms)
  if (answer === 'a') return 'left';
  if (answer === 'b') return 'right';
  if (answer === 'c') return 'both';
  return null;
}

// ─── Expand a region key with laterality ────────────────────────────────────
// e.g. 'arm' with laterality='left' → ['arm_left']
// e.g. 'shoulder' with lateral flag and laterality='both' → ['shoulder_left','shoulder_right']
function expandRegion(regionKey, laterality, isLateral) {
  if (!isLateral) {
    // If the key already ends with _left/_right, keep it.
    // Otherwise return as-is (e.g. 'neck', 'head_top')
    return [regionKey];
  }
  // Strip existing _left/_right suffix if present for re-expansion
  const base = regionKey.replace(/_left$|_right$/, '');
  if (laterality === 'left') return [base + '_left'];
  if (laterality === 'right') return [base + '_right'];
  if (laterality === 'both') return [base + '_left', base + '_right'];
  // Default: show right side (common default)
  return [base + '_right'];
}

// ─── Build resolved annotations for current view ───────────────────────────
function resolveAnnotations(diagnosisIdentifier, symptomLocations, view) {
  const config = DIAGNOSIS_ANNOTATION_MAP[diagnosisIdentifier];
  if (!config) return { dots: [], lines: [] };

  const laterality = resolveLaterality(symptomLocations, config.lateralityKey);
  const dots = [];
  const lines = [];

  // Resolve primary dots
  const dotSources = view === 'back' ? (config.backDots || []) : (config.primaryDots || []);
  for (const regionKey of dotSources) {
    const expanded = expandRegion(regionKey, laterality, !!config.lateralityKey && !regionKey.includes('neck') && !regionKey.includes('head') && !regionKey.includes('shoulder_blade'));
    for (const key of expanded) {
      const coords = BODY_REGION_COORDS[key];
      if (!coords) continue;
      const pos = coords[view];
      if (!pos) continue;
      dots.push({ key, x: pos.x, y: pos.y });
    }
  }

  // Resolve radiation lines
  if (config.radiationTargets) {
    for (const rad of config.radiationTargets) {
      const fromRegions = expandRegion(rad.from, laterality, rad.lateral);
      const toRegions = expandRegion(rad.to, laterality, rad.lateral);

      for (let i = 0; i < Math.max(fromRegions.length, toRegions.length); i++) {
        const fromKey = fromRegions[Math.min(i, fromRegions.length - 1)];
        const toKey = toRegions[Math.min(i, toRegions.length - 1)];
        const fromCoords = BODY_REGION_COORDS[fromKey];
        const toCoords = BODY_REGION_COORDS[toKey];
        if (!fromCoords || !toCoords) continue;
        const fromPos = fromCoords[view];
        const toPos = toCoords[view];
        if (!fromPos || !toPos) continue;
        lines.push({
          key: fromKey + '->' + toKey,
          x1: fromPos.x, y1: fromPos.y,
          x2: toPos.x, y2: toPos.y,
        });
      }
    }
  }

  return { dots, lines };
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const pulseKeyframes = `
@keyframes bodymap-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(251,116,26,0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(251,116,26,0); }
  100% { box-shadow: 0 0 0 0 rgba(251,116,26,0); }
}
@keyframes bodymap-dash {
  to { stroke-dashoffset: -20; }
}
`;

const DOT_COLOR = '#fb741a';
const DOT_SIZE = 'clamp(8px, 1.1vw, 14px)';

// ─── Component ────────────────────────────────────────────────────────────────
export default function BodyMapOverlay({ diagnosisIdentifier, symptomLocations, style, fitHeight }) {
  const [view, setView] = React.useState('back');
  const { dots, lines } = resolveAnnotations(diagnosisIdentifier, symptomLocations, view);
  const imgSrc = view === 'front' ? frontImage : backImage;

  return (
    <Box
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderRadius: 14,
        background: 'rgba(255,255,255,0.75)',
        boxShadow: '0 4px 20px rgba(0,45,85,0.10)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Inject keyframes */}
      <style>{pulseKeyframes}</style>

      {/* Toggle button */}
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px 0 14px' }}>
        <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 'clamp(11px, 0.9vw, 14px)' }}>
          {view === 'front' ? 'Front view' : 'Back view'}
        </Typography>
        <Tooltip title={view === 'front' ? 'Show back view' : 'Show front view'} arrow>
          <IconButton
            size="small"
            onClick={() => setView(v => v === 'front' ? 'back' : 'front')}
            style={{ color: '#002D55' }}
            aria-label="Toggle body view"
          >
            <LoopIcon style={{ fontSize: 'clamp(16px, 1.3vw, 22px)' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Image + overlay container */}
      <Box style={{ position: 'relative', width: '100%', flex: fitHeight ? '1 1 0' : undefined, minHeight: 0, overflow: 'hidden' }}>
        <img
          src={imgSrc}
          alt={view === 'front' ? 'Body front view' : 'Body back view'}
          style={{
            width: '100%',
            height: fitHeight ? '100%' : 'auto',
            objectFit: fitHeight ? 'contain' : undefined,
            display: 'block',
            transition: 'opacity 0.3s ease',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
          draggable={false}
        />

        {/* SVG overlay for radiation lines */}
        {lines.length > 0 && (
          <svg
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '100%', height: '100%',
              pointerEvents: 'none',
            }}
          >
            {lines.map((line) => (
              <line
                key={line.key}
                x1={`${line.x1}%`} y1={`${line.y1}%`}
                x2={`${line.x2}%`} y2={`${line.y2}%`}
                stroke={DOT_COLOR}
                strokeOpacity="0.55"
                strokeWidth="2"
                strokeDasharray="6 4"
                strokeLinecap="round"
                style={{ animation: 'bodymap-dash 1.5s linear infinite' }}
              />
            ))}
          </svg>
        )}

        {/* Dot annotations */}
        {dots.map((dot) => (
          <Box
            key={dot.key}
            style={{
              position: 'absolute',
              left: dot.x + '%',
              top: dot.y + '%',
              width: DOT_SIZE,
              height: DOT_SIZE,
              borderRadius: '50%',
              backgroundColor: DOT_COLOR,
              transform: 'translate(-50%, -50%)',
              animation: 'bodymap-pulse 2s infinite',
              boxShadow: '0 0 6px rgba(251,116,26,0.5)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />
        ))}
      </Box>

      {/* Legend */}
      {dots.length > 0 && (
        <Box style={{ padding: '4px 14px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: DOT_COLOR, flexShrink: 0 }} />
          <Typography style={{ color: '#666', fontSize: 'clamp(9px, 0.7vw, 11px)', lineHeight: 1.3 }}>
            Symptom region
          </Typography>
          {lines.length > 0 && (
            <>
              <Box style={{ width: 18, height: 2, background: `linear-gradient(90deg, ${DOT_COLOR}, transparent)`, flexShrink: 0, marginLeft: 6 }} />
              <Typography style={{ color: '#666', fontSize: 'clamp(9px, 0.7vw, 11px)', lineHeight: 1.3 }}>
                Radiation
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
